/* eslint-disable */
import Vue from 'vue'
import App from './app.vue'
import router from './router'
import store from './store'

// --- PLUGINS & UI LIBRARIES ---
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import VTooltip from 'v-tooltip'
import VPopover from 'vue-js-popover'
import vClickOutside from 'v-click-outside'
import VueSkeletonLoader from 'skeleton-loader-vue'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import ConfirmationService from 'primevue/confirmationservice'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import VueTheMask from 'vue-the-mask'
import 'material-design-icons-iconfont/dist/material-design-icons.css'
import VueTour from 'vue-tour'
import 'vue-tour/dist/vue-tour.css'

// --- UTILS & SERVICES ---
import { isMobileDevice } from "@/utils/pwa-support";
import './utils/validation-rules'
import { initOnboardingToLocalStorage } from '@/services/onboarding-tour'

// --- STYLES ---
import 'primevue/resources/themes/saga-blue/theme.css'
import 'primevue/resources/primevue.min.css'
import 'primeicons/primeicons.css'
import './assets/styles/main.css'
import { Table, Switch, Pagination } from '@oruga-ui/oruga'
import '@oruga-ui/oruga/dist/oruga.css'
import '@oruga-ui/oruga/dist/oruga-full-vars.css'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import VueQRCodeComponent from 'vue-qrcode-component'

// --- PERMISSIONS (CASL) ---
import { abilitiesPlugin } from '@casl/vue'
import defineAbilityFor from './services/defineAbilityFor'
import config from '@/utils/appConfig'

// --- PRERENDER CONFIGURATION ---
// Check if the application is running inside the Prerender SPA Puppeteer instance
const isPrerender = !!window.__PRERENDER_INJECTED;

// Global mixin to allow any component to check 'this.isPrerender'
Vue.mixin({
  computed: {
    isPrerender: () => isPrerender
  }
});

// --- COMPONENT REGISTRATION ---
Vue.component('v-select', vSelect)
Vue.component('DatePicker', DatePicker)
Vue.component('VueCtkDateTimePicker', VueCtkDateTimePicker)
Vue.component('vue-skeleton-loader', VueSkeletonLoader)
Vue.component('qr-code', VueQRCodeComponent)

Vue.use(PrimeVue)
Vue.use(config)
Vue.use(Table)
Vue.use(Pagination)
Vue.use(VueCtkDateTimePicker)
Vue.use(Switch)
Vue.use(VueTheMask)
Vue.use(ToastService)
Vue.use(ConfirmationService)
Vue.use(vClickOutside)
Vue.use(VueTour)

Vue.use(VTooltip, { defaultHtml: false })
Vue.use(VPopover, { tooltip: true })

Vue.use(abilitiesPlugin, defineAbilityFor(), {
  useGlobalProperties: true,
})

Vue.prototype.$showPrimeToast = function (severity, summary, detail, life = 3000) {
  this.$toast.add({ severity, summary, detail, life })
}

Vue.config.productionTip = false

// --- APPLICATION INITIALIZATION ---
function initApp() {
  new Vue({
    router,
    store,
    render: h => h(App),
    mounted() {
      this.$nextTick(() => {
        const style = document.getElementById('hide-app-style');
        if (style) {
          console.log('[Hydration] Revealing app content...');
          style.remove();
        }
      });
    }
  }).$mount('#app')
}

/**
 * Startup Logic:
 * 1. If Prerender: Start app immediately without checking tokens (bot view).
 * 2. If Real User with Token: Fetch profile data first, then init app.
 * 3. If Real User without Token: Start app directly.
 */
if (isPrerender) {
  initApp();
} else {
  const token = localStorage.getItem('token');
  // Extra safety check for "undefined" string values
  if (token && token !== "undefined") {
    store.dispatch('user/initToken').then(() => {
      store.dispatch('user/getMe').then(() => {
        initApp();
      }).catch(() => initApp());
    }).catch(() => initApp());
  } else {
    initApp();
  }
}

// Service Worker (Mobile-only, disabled during prerendering)
if ('serviceWorker' in navigator && isMobileDevice() && !isPrerender) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
        .then(reg => console.log('Service Worker registered:', reg.scope))
        .catch(err => console.warn('Service Worker registration failed:', err));
  });
}

// Initialize Onboarding state (Only for real users to avoid capturing tooltips in SEO snapshots)
if (!isPrerender) {
  initOnboardingToLocalStorage();
}
